import axios, { AxiosError, Canceler } from 'axios';

import { NonNullableAppStore } from 'src/app/store';
import defaultError from 'src/components/Notifications/DefaultError';
import type { AddNotification } from 'src/components/Notifications/Provider/types';
import { Tariff, updateZpTariff, ZpProductParams } from 'src/models/price/priceZp';
import { TariffProductType } from 'src/models/price/product.types';
import { ZpTariff } from 'src/models/price/zpTariff';
import fetcher from 'src/utils/fetcher';

import invalidDateInterval from 'src/api/price/InvalidDateInterval';

declare global {
    interface FetcherGetApi {
        '/shards/price/zp_product': {
            queryParams: ZpProductParams & {
                tariffName: string;
                isBackoffice: boolean;
                priceRegionId: string;
                employerId?: string;
            };
            response: { product: TariffProductType | null };
        };
    }
}

const cancelPreviousRequest: Partial<Record<ZpTariff, Canceler>> = {};

const changeTariffParams =
    (tariff: Tariff, params: ZpProductParams, addNotification: AddNotification) =>
    (dispatch: (action: unknown) => void, getState: () => NonNullableAppStore): unknown => {
        const { priceEmployerId: employerId, priceRegionId } = getState().priceData;
        const { tariffName, backoffice: isBackoffice } = tariff;

        const newParams = { ...tariff.params, ...params };
        dispatch(updateZpTariff({ tariffName, isBackoffice, params: newParams }));

        // Если какие-то поля отсутствуют, не делаем запрос.
        if (Object.values(newParams).some((value) => value === null)) {
            return Promise.resolve();
        }

        const source = axios.CancelToken.source();
        cancelPreviousRequest[tariffName]?.();
        cancelPreviousRequest[tariffName] = source.cancel;

        dispatch(updateZpTariff({ tariffName, isBackoffice, loading: true }));
        return fetcher
            .get('/shards/price/zp_product', {
                params: {
                    tariffName,
                    isBackoffice,
                    priceRegionId,
                    ...newParams,
                    employerId,
                },
                cancelToken: source.token,
            })
            .then(
                (data) => {
                    dispatch(updateZpTariff({ tariffName, isBackoffice, product: data.product, loading: false }));
                },
                (reason: AxiosError) => {
                    if (fetcher.isCancel(reason)) {
                        return;
                    }
                    if (reason.response?.data === 'INVALID_DATE_INTERVAL') {
                        addNotification(invalidDateInterval);
                    } else {
                        addNotification(defaultError);
                    }
                    dispatch(updateZpTariff({ tariffName, isBackoffice, loading: false }));
                }
            );
    };

export default changeTariffParams;
